import React, { forwardRef, useState, type ButtonHTMLAttributes } from 'react';

/**
 * Underline tab bar — the top-level "switch between views of a page" idiom
 * (e.g. Local Data / Remote Data). For a bordered/segmented selector that
 * picks a filter or mode, use {@link ToggleButton} instead.
 *
 * `Tabs` renders a `role="tablist"` row of `Tab` buttons with an underline
 * active indicator; `Tab` can also be used standalone when the set of tabs is
 * not a simple array.
 */

export interface TabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether this tab is the currently selected one. */
  active: boolean;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { active, children, style, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={active}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        background: 'transparent',
        color: active ? 'var(--accent)' : hover ? 'var(--text-primary)' : 'var(--text-secondary)',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        transition: 'color 0.15s',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

export interface TabItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  /** Optional tooltip shown on hover. */
  title?: string;
}

export interface TabsProps<T extends string = string> {
  tabs: ReadonlyArray<TabItem<T>>;
  active: T;
  onChange: (id: T) => void;
  /** When set, the tablist is labelled for screen readers. */
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function Tabs<T extends string = string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  className,
  style,
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`ui-tabbar${className ? ` ${className}` : ''}`}
      style={{ gap: 4, ...style }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.id}
          active={t.id === active}
          title={t.title}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </Tab>
      ))}
    </div>
  );
}
